import { MouseEvent, useCallback, useState } from 'react';

import classNames from 'classnames';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Avatar } from '@vk-hr-tek/ui/Avatar';
import { Box } from '@vk-hr-tek/ui/Box';
import { getInitials } from '@vk-hr-tek/ui/format';
import { Icon } from '@vk-hr-tek/ui/Icon';

import { useSelector } from '@app/hooks';

import { UserApiService } from '../../../user/services';
import { selectFullName, selectUser } from '../../../user';
import { UserCard } from '../common/UserCard';

import { LogoutConfirmation } from './LogoutConfirmation';
import { useStyles } from './User.styles';

interface UserProps {
  userRepresentative: boolean;
}

export const User = ({ userRepresentative = false }: UserProps) => {
  const { classes } = useStyles();
  const userApiService = useInject(UserApiService);

  const userFullName = useSelector(selectFullName);
  const user = useSelector(selectUser);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const loadPhoto = useCallback(async () => {
    const res = await userApiService.getUserPhoto({ user_id: user?.id ?? '' });

    return res.file;
  }, [userApiService, user?.id]);

  const handleProfileMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuQuitClick = useCallback(() => {
    handleMenuClose();
    setIsConfirmationDialogOpen(true);
  }, []);

  const handleLogoutConfirmationClose = useCallback(
    () => setIsConfirmationDialogOpen(false),
    [],
  );

  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'primary-search-account-menu';

  return (
    <Box display="flex" alignItems="center">
      <Box display="inline" ml="8">
        <div className={classes.avatar} onClick={handleProfileMenuOpen}>
          <Avatar
            loadPhoto={loadPhoto}
            children={getInitials(userFullName || '')}
          />
          <Box p="4" display="flex" alignItems="center" justifyContent="center">
            <Icon name="dropdown_outline" color="stroke.secondary" size="16" />
          </Box>
        </div>
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 60,
          horizontal: 'right',
        }}
        id={menuId}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{ list: classes.menu }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        {(!window.REACT_APP_VKHRTEK_LK_DISABLED || userRepresentative) && (
          <UserCard
            onClick={handleMenuClose}
            userFullName={userFullName || ''}
          />
        )}

        <MenuItem
          onClick={handleMenuQuitClick}
          className={classNames(classes.link, classes.link_type_logout)}
        >
          <Box display="inline" mr="8" lineHeight={1}>
            <Icon name="door_enter_arrow_right_outline" size="16" />
          </Box>
          Выйти
        </MenuItem>
      </Menu>
      <LogoutConfirmation
        onClose={handleLogoutConfirmationClose}
        open={isConfirmationDialogOpen}
      />
    </Box>
  );
};
